"use client";

import Link from "next/link";

import { cn } from "@/lib/utils";
import type { BannerState } from "@/hooks/use-assessment-rhythm-bridge";
import { Button } from "@/components/ui/button";

interface AssessmentRhythmBannerProps {
  state: BannerState;
  onAction?: () => void;
  onRemindLater?: () => void;
  onDismiss?: () => void;
}

const VARIANT_CLASS: Record<
  NonNullable<BannerState["variant"]>,
  {
    wrapper: string;
    pill: string;
    action: string;
  }
> = {
  info: {
    wrapper:
      "border-sky-100 bg-gradient-to-r from-sky-50/90 via-white to-emerald-50 shadow-[0_15px_35px_-20px_rgba(15,118,230,0.45)]",
    pill: "bg-sky-100 text-sky-900",
    action:
      "bg-gradient-to-r from-amber-100 via-orange-100 to-rose-100 text-amber-900 border border-amber-100 shadow-md hover:from-amber-200 hover:via-orange-200 hover:to-rose-200 dark:from-amber-400/30 dark:via-orange-400/30 dark:to-rose-400/30 dark:text-white",
  },
  success: {
    wrapper:
      "border-emerald-100 bg-gradient-to-r from-emerald-50/90 via-white to-lime-50 shadow-[0_15px_35px_-20px_rgba(16,185,129,0.4)]",
    pill: "bg-emerald-100 text-emerald-900",
    action:
      "bg-gradient-to-r from-amber-100 via-orange-100 to-rose-100 text-amber-900 border border-amber-100 shadow-md hover:from-amber-200 hover:via-orange-200 hover:to-rose-200 dark:from-amber-400/30 dark:via-orange-400/30 dark:to-rose-400/30 dark:text-white",
  },
  warning: {
    wrapper:
      "border-amber-100 bg-gradient-to-r from-amber-50/90 via-white to-rose-50 shadow-[0_15px_35px_-20px_rgba(251,191,36,0.45)]",
    pill: "bg-amber-100 text-amber-900",
    action:
      "bg-gradient-to-r from-amber-100 via-orange-100 to-rose-100 text-amber-900 border border-amber-100 shadow-md hover:from-amber-200 hover:via-orange-200 hover:to-rose-200 dark:from-amber-400/30 dark:via-orange-400/30 dark:to-rose-400/30 dark:text-white",
  },
};

export function AssessmentRhythmBanner({
  state,
  onAction,
  onRemindLater,
  onDismiss,
}: AssessmentRhythmBannerProps) {
  if (!state.visible) return null;

  const palette = VARIANT_CLASS[state.variant ?? "info"];

  const actionButton = state.actionHref ? (
    <Button
      asChild
      size="sm"
      className={cn("rounded-full px-4 font-semibold", palette.action)}
      onClick={onAction}
    >
      <Link href={state.actionHref}>{state.actionLabel}</Link>
    </Button>
  ) : (
    <Button
      size="sm"
      className={cn("rounded-full px-4 font-semibold", palette.action)}
      onClick={onAction}
    >
      {state.actionLabel}
    </Button>
  );

  return (
    <section
      className={cn(
        "relative overflow-hidden rounded-3xl border px-5 py-4 text-sm text-slate-800 backdrop-blur",
        palette.wrapper,
      )}
    >
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute -top-10 left-6 h-24 w-24 rounded-full bg-white/40 blur-3xl" />
        <div className="absolute -bottom-12 right-8 h-28 w-28 rounded-full bg-white/30 blur-3xl" />
      </div>
      <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "rounded-full px-3 py-1 text-xs font-semibold",
                palette.pill,
              )}
            >
              {state.title}
            </span>
            {state.updatedAtLabel && (
              <span className="text-xs text-slate-500">
                · {state.updatedAtLabel}
              </span>
            )}
          </div>
          <p className="text-body font-medium text-slate-900">
            {state.description}
          </p>
          {(state.nextHintLabel || state.statusLabel) && (
            <div className="text-xs text-slate-600">
              {state.statusLabel}
              {state.statusLabel && state.nextHintLabel ? " · " : ""}
              {state.nextHintLabel}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 text-xs font-medium text-slate-600 md:text-right">
          <div className="flex flex-wrap items-center gap-2">
            {actionButton}
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full px-3 text-slate-500 hover:text-slate-900"
              onClick={onRemindLater}
            >
              {state.remindLabel}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full px-3 text-slate-400 hover:text-slate-800"
              onClick={onDismiss}
            >
              ✕
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
